import { useState } from "react";
import { Anchor, Box, Group, Text, ActionIcon, Collapse, CopyButton, Flex, Tooltip } from "@mantine/core";
import { IconCheck, IconChevronDown, IconChevronRight } from "@tabler/icons-react";
import classes from "./smaui-code-preview.module.css";

const INDENT_SIZE = 4;

const cx = (...values) => values.filter(Boolean).join(" ") || undefined;

const ATTR_REGEX = /^(?:href|src|alt|id|class|className|style|type|name|value|placeholder|action|method|target|rel|for|htmlFor|onClick|onChange|onSubmit|onLoad|onError|disabled|checked|selected|required|readonly|multiple|autoFocus|autoComplete|tabIndex|role|aria-label|aria-hidden|aria-describedby|data-[a-zA-Z-]+|xmlns|charset|content|http-equiv|lang|dir|draggable|hidden|title|width|height|viewBox|fill|stroke|integrity|crossorigin|nonce|sandbox|allow|allowfullscreen|frameborder|scrolling|srcdoc|loading|decoding|fetchpriority|sizes|srcset|media|as|defer|async|nomodule)\b/;
const TAG_OPEN = /^<\/?[A-Za-z][A-Za-z0-9.]*/;
const TAG_CLOSE = /^\/?>/;

const RULES = [
    { type: "comment", regex: /^\/\/[^\n]*|^\/\*[\s\S]*?\*\/|^<!--[\s\S]*?-->/ },
    { type: "string", regex: /^"(?:\\.|[^"\\])*"|^'(?:\\.|[^'\\])*'|^`(?:\\.|[^`\\])*`/ },
    { type: "keyword", regex: /^(const|let|var|function|return|if|else|for|while|import|export|default|class|new|this|typeof|from|of|in|async|await|try|catch|throw)\b/ },
    { type: "number", regex: /^\b\d+(\.\d+)?\b/ },
    { type: "bracket", regex: /^[{}[\]()]/ },
    { type: "operator", regex: /^[=+\-*/<>!&|?:]+/ },
];

const tokenize = (code) => {
    const tokens = [];
    let remaining = code;
    let inTag = false;

    while (remaining.length > 0) {
        const tagOpenMatch = remaining.match(TAG_OPEN);
        if (tagOpenMatch) {
            inTag = true;
            tokens.push({ type: "tag", value: tagOpenMatch[0] });
            remaining = remaining.slice(tagOpenMatch[0].length);
            continue;
        }

        const tagCloseMatch = remaining.match(TAG_CLOSE);
        if (tagCloseMatch && inTag) {
            inTag = false;
            tokens.push({ type: "tag", value: tagCloseMatch[0] });
            remaining = remaining.slice(tagCloseMatch[0].length);
            continue;
        }

        if (inTag) {
            const attrMatch = remaining.match(ATTR_REGEX);
            if (attrMatch) {
                tokens.push({ type: "attr", value: attrMatch[0] });
                remaining = remaining.slice(attrMatch[0].length);
                continue;
            }
        }

        let matched = false;
        for (const { type, regex } of RULES) {
            const match = remaining.match(regex);
            if (match) {
                tokens.push({ type, value: match[0] });
                remaining = remaining.slice(match[0].length);
                matched = true;
                break;
            }
        }

        if (!matched) {
            if (tokens.length > 0 && tokens[tokens.length - 1].type === "plain") {
                tokens[tokens.length - 1].value += remaining[0];
            } else {
                tokens.push({ type: "plain", value: remaining[0] });
            }
            remaining = remaining.slice(1);
        }
    }

    return tokens;
};

const parseLines = (code) =>
    code.split("\n").map((line) => {
        const rawIndent = line.match(/^(\s*)/)?.[1] ?? "";
        const spaces = rawIndent.replace(/\t/g, " ".repeat(INDENT_SIZE));
        const level = Math.floor(spaces.length / INDENT_SIZE);
        const tokens = tokenize(line.slice(rawIndent.length));
        return { level, tokens };
    });

const GUIDE_COLORS = ["#3b4261", "#2d3a52", "#2a3a4a", "#2a3d3d"];

const IndentGuides = ({ level }) =>
    Array.from({ length: level }, (_, i) => (
        <span
            key={i}
            className={classes.indentGuide}
            style={{ borderLeftColor: GUIDE_COLORS[i % GUIDE_COLORS.length] }}
        />
    ));

const CodeLine = ({ line }) => (
    <div className={classes.line}>
        <IndentGuides level={line.level} />
        <span className={classes.lineContent}>
            {line.tokens.map((token, i) => (
                <span key={i} className={classes[token.type]}>
                    {token.value}
                </span>
            ))}
        </span>
    </div>
);

export const SmauiCodePreview = ({
    code = "",
    label,
    docsUrl,
    defaultExpanded = true,
    language = "javascript",
    classNames = {},
    labels = {},
    ...rest
}) => {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const lines = parseLines(code);

    return (
        <Box className={cx(classes.wrapper, classNames.wrapper)}>
            <Group
                justify="space-between"
                className={classNames.header}
                onClick={() => setExpanded((v) => !v)}
            >

                {label && (
                    <Group gap={12}>
                        <Text className={cx(classes.label, classNames.label)}>{label}</Text>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M8.00065 14.6678C11.6825 14.6678 14.6673 11.683 14.6673 8.00114C14.6673 4.31924 11.6825 1.33447 8.00065 1.33447C4.31875 1.33447 1.33398 4.31924 1.33398 8.00114C1.33398 11.683 4.31875 14.6678 8.00065 14.6678Z" stroke="#787878" />
                            <path d="M6.66699 6.00033C6.66699 5.26395 7.26393 4.66699 8.00033 4.66699C8.73673 4.66699 9.33366 5.26395 9.33366 6.00033C9.33366 6.26576 9.25613 6.51308 9.12239 6.72086C8.72393 7.34013 8.00033 7.93059 8.00033 8.66699V9.00033" stroke="#787878" stroke-linecap="round" />
                            <path d="M7.99512 11.3345H8.00232" stroke="#787878" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                    </Group>
                )}

                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M5 12.5L10 7.5L15 12.5" stroke="#333333" stroke-width="1.25" stroke-miterlimit="16" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
            </Group>

            <Collapse in={expanded}>
                <Box className={cx(classes.root, classNames.root)} {...rest}>
                    <Flex gap={10}>
                        <pre>
                            {lines.map((line, i) => (
                                <CodeLine key={i} line={line} />
                            ))}
                        </pre>
                        <CopyButton value={code} timeout={2000}>
                            {({ copied, copy }) => (
                                <Tooltip label={copied ? (labels.copied || "Copied") : (labels.copy || "Copy")} withArrow>
                                    <ActionIcon variant="transparent" color="dark" aria-label="Copy" onClick={copy}>
                                        {copied ? <IconCheck size={20} /> : (
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                                <path d="M9.16667 18.3331C8.24619 18.3331 7.5 17.587 7.5 16.6665V9.16764C7.5 8.24671 8.24691 7.50033 9.16783 7.50098L16.6678 7.50627C17.5878 7.50692 18.3333 8.25292 18.3333 9.17297V16.6665C18.3333 17.587 17.5872 18.3331 16.6667 18.3331H9.16667Z" stroke="#1A1B1F" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
                                                <path d="M14.167 7.02394V3.34103C14.167 2.421 13.4215 1.675 12.5015 1.67436L3.33483 1.66797C2.41389 1.66733 1.66699 2.4137 1.66699 3.33464V12.5001C1.66699 13.4206 2.41318 14.1668 3.33366 14.1668H7.02413" stroke="#1A1B1F" stroke-width="1.25" stroke-linejoin="round" />
                                            </svg>
                                        )}
                                    </ActionIcon>
                                </Tooltip>
                            )}
                        </CopyButton>
                    </Flex>
                    {docsUrl && (
                        <Group justify="flex-end" className={classNames.docs}>
                            <Anchor href={docsUrl} target="_blank" rel="noopener noreferrer" underline="never" bg="#fff" px={6} style={{ borderRadius: 6 }}>
                                <Group gap={6}>
                                    <Text fz={14} fw={400} c="#24292E" td="underline">
                                        {labels.docs || "How to use this script"}
                                    </Text>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                        <path d="M6.12503 1.75H2.91667C2.27233 1.75 1.75 2.27233 1.75 2.91667V11.0833C1.75 11.7277 2.27233 12.25 2.91667 12.25H11.0834C11.7277 12.25 12.25 11.7277 12.25 11.0833V7.875" stroke="#24292E" stroke-linecap="round" stroke-linejoin="round" />
                                        <path d="M6.41699 7.58333L11.8711 2.12884M12.2503 5.25V1.75H8.75033" stroke="#24292E" stroke-linecap="round" stroke-linejoin="round" />
                                    </svg>
                                </Group>
                            </Anchor>
                        </Group>
                    )}
                </Box>
            </Collapse >
        </Box >
    );
};